import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Plus, Pencil, Trash2, Search, UserCircle, Mail, Shield, Calendar, Users, Loader2, AlertCircle } from "lucide-react";
import { mockUsers, mockUserProfiles } from "@/data/mockUserProfiles";
import { User } from "@/types/permissions";
import { toast } from "sonner";
import { format } from "date-fns";

// Workspace user interface
interface WorkspaceUser {
  id: string;
  name: string;
  email: string;
  department?: string;
  avatarUrl?: string;
}

// User Dialog Component (extracted to prevent re-renders)
interface UserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  formData: {
    name: string;
    email: string;
    profileId: string;
    isActive: boolean;
  };
  setFormData: React.Dispatch<React.SetStateAction<{
    name: string;
    email: string;
    profileId: string;
    isActive: boolean;
  }>>;
  onSave: () => void;
}

const UserDialog = ({ open, onOpenChange, title, formData, setFormData, onSave }: UserDialogProps) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>Configure user details and assign a profile</DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="name">Full Name *</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., John Smith"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">Email Address *</Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="e.g., john.smith@company.com"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="profile">User Profile *</Label>
          <Select value={formData.profileId} onValueChange={(value) => setFormData({ ...formData, profileId: value })}>
            <SelectTrigger id="profile">
              <SelectValue placeholder="Select a profile" />
            </SelectTrigger>
            <SelectContent>
              {mockUserProfiles.map((profile) => (
                <SelectItem key={profile.id} value={profile.id}>
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    <span>{profile.name}</span>
                    {profile.isSystem && <Badge variant="outline" className="ml-2">System</Badge>}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {formData.profileId && (
            <p className="text-sm text-muted-foreground">
              {mockUserProfiles.find((p) => p.id === formData.profileId)?.description}
            </p>
          )}
        </div>

        <div className="flex items-center justify-between space-x-2">
          <div className="space-y-0.5">
            <Label htmlFor="active">Active Status</Label>
            <p className="text-sm text-muted-foreground">User can access the system</p>
          </div>
          <Switch
            id="active"
            checked={formData.isActive}
            onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
          />
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button onClick={onSave}>Save User</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export function UsersPage() {
  const [users, setUsers] = useState<User[]>(mockUsers);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterProfile, setFilterProfile] = useState<string>("all");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    profileId: "",
    isActive: true,
  });

  // Workspace import state
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [workspaceUsers, setWorkspaceUsers] = useState<WorkspaceUser[]>([]);
  const [isLoadingWorkspaceUsers, setIsLoadingWorkspaceUsers] = useState(false);
  const [workspaceError, setWorkspaceError] = useState<string | null>(null);
  const [workspaceSearchQuery, setWorkspaceSearchQuery] = useState("");
  const [selectedWorkspaceUsers, setSelectedWorkspaceUsers] = useState<string[]>([]);
  const [importProfileId, setImportProfileId] = useState("");
  const [hideAlreadyAdded, setHideAlreadyAdded] = useState(true);

  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesProfile = filterProfile === "all" || user.profileId === filterProfile;
    const matchesStatus = filterStatus === "all" || (filterStatus === "active" ? user.isActive : !user.isActive);

    return matchesSearch && matchesProfile && matchesStatus;
  });

  const handleCreate = () => {
    setFormData({ name: "", email: "", profileId: "", isActive: true });
    setIsCreateDialogOpen(true);
  };

  const handleImportFromWorkspace = () => {
    setIsImportDialogOpen(true);
    setSelectedWorkspaceUsers([]);
    setImportProfileId("");
    setWorkspaceSearchQuery("");
    fetchWorkspaceUsers();
  };

  const fetchWorkspaceUsers = async () => {
    setIsLoadingWorkspaceUsers(true);
    setWorkspaceError(null);
    
    // Simulate API delay for realistic UX
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Using mock data for demo (no backend required)
    console.log('Loading mock workspace users for demo');
    setWorkspaceUsers([
      { id: 'ws-1', name: 'Alice Johnson', email: 'alice.johnson@company.com', department: 'Engineering' },
      { id: 'ws-2', name: 'Bob Smith', email: 'bob.smith@company.com', department: 'Marketing' },
      { id: 'ws-3', name: 'Carol White', email: 'carol.white@company.com', department: 'Sales' },
      { id: 'ws-4', name: 'David Brown', email: 'david.brown@company.com', department: 'Engineering' },
      { id: 'ws-5', name: 'Emma Davis', email: 'emma.davis@company.com', department: 'HR' },
      { id: 'ws-6', name: 'Frank Miller', email: 'frank.miller@company.com', department: 'Finance' },
    ]);
    
    setIsLoadingWorkspaceUsers(false);
  };

  const toggleWorkspaceUserSelection = (userId: string) => {
    if (selectedWorkspaceUsers.includes(userId)) {
      setSelectedWorkspaceUsers(selectedWorkspaceUsers.filter(id => id !== userId));
    } else {
      setSelectedWorkspaceUsers([...selectedWorkspaceUsers, userId]);
    }
  };

  const isUserAlreadyAdded = (email: string) => {
    return users.some(u => u.email.toLowerCase() === email.toLowerCase());
  };

  const filteredWorkspaceUsers = workspaceUsers.filter((user) => {
    const matchesSearch =
      user.name.toLowerCase().includes(workspaceSearchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(workspaceSearchQuery.toLowerCase()) ||
      (user.department?.toLowerCase().includes(workspaceSearchQuery.toLowerCase()) ?? false);
    
    const isAlreadyAdded = isUserAlreadyAdded(user.email);
    const matchesFilter = !hideAlreadyAdded || !isAlreadyAdded;

    return matchesSearch && matchesFilter;
  });

  const handleImportUsers = () => {
    if (selectedWorkspaceUsers.length === 0) {
      toast.error("Please select at least one user to import");
      return;
    }

    if (!importProfileId) {
      toast.error("Please select a profile for the imported users");
      return;
    }

    const selectedUsers = workspaceUsers.filter(wu => selectedWorkspaceUsers.includes(wu.id));
    const newUsers: User[] = [];
    const skippedUsers: string[] = [];

    selectedUsers.forEach(wu => {
      if (isUserAlreadyAdded(wu.email)) {
        skippedUsers.push(wu.name);
      } else {
        newUsers.push({
          id: `user-${Date.now()}-${wu.id}`,
          name: wu.name,
          email: wu.email,
          profileId: importProfileId,
          isActive: true,
          createdAt: new Date().toISOString(),
        });
      }
    });

    if (newUsers.length > 0) {
      setUsers([...users, ...newUsers]);
      toast.success(`Successfully imported ${newUsers.length} user${newUsers.length > 1 ? 's' : ''}`);
    }

    if (skippedUsers.length > 0) {
      toast.warning(`Skipped ${skippedUsers.length} user${skippedUsers.length > 1 ? 's' : ''} (already added): ${skippedUsers.join(', ')}`);
    }

    setIsImportDialogOpen(false);
    setSelectedWorkspaceUsers([]);
    setImportProfileId("");
  };

  const handleEdit = (user: User) => {
    setSelectedUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      profileId: user.profileId,
      isActive: user.isActive,
    });
    setIsEditDialogOpen(true);
  };

  const handleDelete = (user: User) => {
    setSelectedUser(user);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (selectedUser) {
      setUsers(users.filter((u) => u.id !== selectedUser.id));
      toast.success("User deleted successfully");
      setIsDeleteDialogOpen(false);
      setSelectedUser(null);
    }
  };

  const saveUser = () => {
    if (!formData.name.trim()) {
      toast.error("User name is required");
      return;
    }

    if (!formData.email.trim()) {
      toast.error("Email is required");
      return;
    }

    if (!formData.profileId) {
      toast.error("User profile is required");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    if (isEditDialogOpen && selectedUser) {
      // Update existing user
      setUsers(
        users.map((u) =>
          u.id === selectedUser.id
            ? {
                ...u,
                name: formData.name,
                email: formData.email,
                profileId: formData.profileId,
                isActive: formData.isActive,
              }
            : u
        )
      );
      toast.success("User updated successfully");
      setIsEditDialogOpen(false);
    } else {
      // Check if email already exists
      if (users.some((u) => u.email.toLowerCase() === formData.email.toLowerCase())) {
        toast.error("A user with this email already exists");
        return;
      }

      // Create new user
      const newUser: User = {
        id: `user-${Date.now()}`,
        name: formData.name,
        email: formData.email,
        profileId: formData.profileId,
        isActive: formData.isActive,
        createdAt: new Date().toISOString(),
      };
      setUsers([...users, newUser]);
      toast.success("User created successfully");
      setIsCreateDialogOpen(false);
    }

    setSelectedUser(null);
    setFormData({ name: "", email: "", profileId: "", isActive: true });
  };

  const getProfileName = (profileId: string) => {
    return mockUserProfiles.find((p) => p.id === profileId)?.name || "Unknown";
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Users</h1>
          <p className="text-muted-foreground">Manage user accounts and profile assignments</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleImportFromWorkspace}>
            <Users className="mr-2 h-4 w-4" />
            Import from Access Evo
          </Button>
          <Button onClick={handleCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Add User
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle>All Users</CardTitle>
              <CardDescription>View and manage user accounts</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative w-64">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8"
                />
              </div>
              <Select value={filterProfile} onValueChange={setFilterProfile}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Profiles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Profiles</SelectItem>
                  {mockUserProfiles.map((profile) => (
                    <SelectItem key={profile.id} value={profile.id}>
                      {profile.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Profile</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last Login</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <UserCircle className="h-4 w-4 text-muted-foreground" />
                      {user.name}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Mail className="h-3 w-3" />
                      {user.email}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Shield className="h-3 w-3 text-muted-foreground" />
                      <span className="text-sm">{getProfileName(user.profileId)}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {user.isActive ? (
                      <Badge variant="default" className="bg-green-600">
                        Active
                      </Badge>
                    ) : (
                      <Badge variant="secondary">Inactive</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {user.lastLogin ? (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        {format(new Date(user.lastLogin), "MMM d, yyyy")}
                      </div>
                    ) : (
                      <span className="text-sm text-muted-foreground">Never</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-muted-foreground">
                      {format(new Date(user.createdAt), "MMM d, yyyy")}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(user)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(user)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {filteredUsers.length === 0 && (
            <div className="text-center py-12">
              <UserCircle className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <h3 className="mt-4 text-lg font-semibold">No users found</h3>
              <p className="text-muted-foreground">Try adjusting your search or filters</p>
            </div>
          )}
        </CardContent>
      </Card>

      <UserDialog 
        open={isCreateDialogOpen} 
        onOpenChange={setIsCreateDialogOpen} 
        title="Add New User"
        formData={formData}
        setFormData={setFormData}
        onSave={saveUser}
      />
      <UserDialog 
        open={isEditDialogOpen} 
        onOpenChange={setIsEditDialogOpen} 
        title="Edit User"
        formData={formData}
        setFormData={setFormData}
        onSave={saveUser}
      />

      {/* Import from Workspace Dialog */}
      <Dialog open={isImportDialogOpen} onOpenChange={setIsImportDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Import Users from Access Evo</DialogTitle>
            <DialogDescription>
              Select users from your Access Evo workspace to add to this application
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Search and Filters */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search workspace users..."
                    value={workspaceSearchQuery}
                    onChange={(e) => setWorkspaceSearchQuery(e.target.value)}
                    className="pl-8"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="hideAdded"
                    checked={hideAlreadyAdded}
                    onCheckedChange={(checked) => setHideAlreadyAdded(checked as boolean)}
                  />
                  <Label htmlFor="hideAdded" className="text-sm cursor-pointer">
                    Hide already added
                  </Label>
                </div>
              </div>
              
              {/* Select All */}
              {filteredWorkspaceUsers.length > 0 && (
                <div className="flex items-center gap-2 px-1">
                  <Checkbox
                    id="selectAll"
                    checked={
                      filteredWorkspaceUsers.filter(u => !isUserAlreadyAdded(u.email)).length > 0 &&
                      filteredWorkspaceUsers.filter(u => !isUserAlreadyAdded(u.email)).every(u => selectedWorkspaceUsers.includes(u.id))
                    }
                    onCheckedChange={(checked) => {
                      if (checked) {
                        // Select all non-added users
                        const availableUsers = filteredWorkspaceUsers.filter(u => !isUserAlreadyAdded(u.email));
                        setSelectedWorkspaceUsers(availableUsers.map(u => u.id));
                      } else {
                        // Deselect all
                        setSelectedWorkspaceUsers([]);
                      }
                    }}
                    disabled={filteredWorkspaceUsers.every(u => isUserAlreadyAdded(u.email))}
                  />
                  <Label htmlFor="selectAll" className="text-sm font-medium cursor-pointer">
                    Select all ({filteredWorkspaceUsers.filter(u => !isUserAlreadyAdded(u.email)).length} available)
                  </Label>
                </div>
              )}
            </div>

            {/* Error State - Hidden for demo, using mock data fallback */}

            {/* User List */}
            <ScrollArea className="h-[300px] border rounded-md">
              {isLoadingWorkspaceUsers ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : filteredWorkspaceUsers.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="mx-auto h-12 w-12 text-muted-foreground/50" />
                  <h3 className="mt-4 text-lg font-semibold">No users found</h3>
                  <p className="text-muted-foreground">Try adjusting your search or filters</p>
                </div>
              ) : (
                <div className="p-4 space-y-2">
                  {filteredWorkspaceUsers.map((user) => {
                    const isAlreadyAdded = isUserAlreadyAdded(user.email);
                    const isSelected = selectedWorkspaceUsers.includes(user.id);
                    
                    return (
                      <div
                        key={user.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                          isAlreadyAdded
                            ? 'bg-muted/50 border-muted'
                            : isSelected
                            ? 'bg-primary/5 border-primary'
                            : 'hover:bg-muted/50 border-transparent'
                        }`}
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleWorkspaceUserSelection(user.id)}
                          disabled={isAlreadyAdded}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <UserCircle className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                            <span className="font-medium truncate">{user.name}</span>
                            {isAlreadyAdded && (
                              <Badge variant="secondary" className="ml-auto flex-shrink-0">
                                Already Added
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                            <Mail className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate">{user.email}</span>
                            {user.department && (
                              <>
                                <span>•</span>
                                <span className="truncate">{user.department}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>

            {/* Selected Users Summary */}
            {selectedWorkspaceUsers.length > 0 && (
              <div className="p-3 bg-muted rounded-md">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">
                    {selectedWorkspaceUsers.length} user{selectedWorkspaceUsers.length > 1 ? 's' : ''} selected
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedWorkspaceUsers([])}
                  >
                    Clear selection
                  </Button>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="importProfile">Assign Profile *</Label>
                  <Select value={importProfileId} onValueChange={setImportProfileId}>
                    <SelectTrigger id="importProfile">
                      <SelectValue placeholder="Select a profile for imported users" />
                    </SelectTrigger>
                    <SelectContent>
                      {mockUserProfiles.map((profile) => (
                        <SelectItem key={profile.id} value={profile.id}>
                          <div className="flex items-center gap-2">
                            <Shield className="h-4 w-4" />
                            <span>{profile.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {importProfileId && (
                    <p className="text-sm text-muted-foreground">
                      {mockUserProfiles.find((p) => p.id === importProfileId)?.description}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsImportDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleImportUsers}
              disabled={selectedWorkspaceUsers.length === 0 || !importProfileId}
            >
              Import {selectedWorkspaceUsers.length > 0 ? `${selectedWorkspaceUsers.length} ` : ''}User{selectedWorkspaceUsers.length !== 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedUser?.name}"? This action cannot be undone and will remove all user data and access.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
